import { Skeleton } from 'antd'
import IconBtnZoomIn from 'components/icons/IconBtnZoomIn'
import { SIDE_PANEL_TREE_OPTIONS } from 'constants/common'
import { API_KEY } from 'constants/react-query'
import { PERIOD_TYPE_TRANSLATE } from 'constants/translate'
import { EModalType } from 'enums/common'
import { EKPIUnit } from 'enums/kpi'
import useNotification from 'hooks/useNotification'
import { TChartLayout } from 'models/common'
import { ISidePanelReq } from 'models/dashboard'
import { MouseEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  LegendProps,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts'
import { getDashboardSidePanel } from 'services/dashboard.service'
import { getListOptionValue } from 'utils/common'
import NumberUtil from 'utils/number'
import BaseModal from '../BaseModal'
import BaseTreeSelect from '../BaseSelect/BaseTreeSelect'
import { useTooltipContext } from '../BaseTooltip/CustomTooltip'
import { BaseMultipleChart } from './BaseMultipleChart'
import { renderBarLegend } from './components/BarChartLegend'
import { CustomizedAxisTick } from './components/CustomAxisTick'
import { CustomizedAxisTickOverall } from './components/CustomAxisTickOverall'
import { CustomLabel } from './components/CustomLable'

export interface RechartsProps {
  key: string
  yAxisId?: string
  color: string
  display: boolean
  isHover: boolean
}

interface BaseBarChartProps<T> {
  data: T[]
  layout: TChartLayout
  colors: any
  chartProps: Array<RechartsProps>
  yAxisProps?: {
    data: Array<{
      yAxisId: string
      orientation: 'left' | 'right'
      unit: string
      staffId?: number
      departmentId?: number
    }>
    period?: string[]
  }
  width?: string | number
  height?: string | number
  w?: number
  margin?: {
    top?: number
    bottom?: number
    right?: number
    left?: number
  }
  legendProps?: Pick<
    LegendProps,
    'height' | 'width' | 'layout' | 'align' | 'margin' | 'formatter' | 'iconType' | 'iconSize' | 'onClick'
  >
}

function BaseBarChart<T>({
  data,
  layout,
  colors,
  width = '100%',
  height = '100%',
  w,
  chartProps,
  yAxisProps,
  ...props
}: BaseBarChartProps<T>) {
  const { openTooltip, closeTooltip } = useTooltipContext()
  const n = useNotification()
  const { t } = useTranslation()
  const { control, getValues, setValue, watch, reset } = useForm()

  const [id, setId] = useState<number>()
  const [isPreview, setIsPreview] = useState<boolean>(false)
  const [params, setParams] = useState<ISidePanelReq>({})

  const {
    data: dataChartMultiple,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: [API_KEY.DASHBOARD_SIDE_PANEL, params],
    queryFn: async () => await getDashboardSidePanel(params),
    onError: n.handleUseQueryError(),
    enabled: !!id && !!Object.keys(params).length
  })

  const wPeriod = watch('period')

  useEffect(() => {
    if (id) {
      setParams({
        period: getListOptionValue(wPeriod),
        staffId: getValues('staffId'),
        departmentId: getValues('departmentId')
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wPeriod])

  const tooltipContent = (e: any) => {
    return (
      <span>
        {e?.dataKey ? (e?.dataKey === 'qAVG' ? PERIOD_TYPE_TRANSLATE.Overall : e?.dataKey) + ': ' : ''}
        {e && NumberUtil.format(e?.value?.toFixed(2), { unit: EKPIUnit.PERCENT })}
      </span>
    )
  }

  const handleClickLable = (e: MouseEvent<SVGElement, globalThis.MouseEvent>) => {
    const index = (e as any)?.index
    const staffId = yAxisProps?.data?.[index]?.staffId
    const departmentId = yAxisProps?.data?.[index]?.departmentId
    const staffOrDeptId = staffId || departmentId
    setId(staffOrDeptId)
    setIsPreview(true)
    setValue('period', yAxisProps?.period)
    if (staffId) {
      setValue('staffId', staffOrDeptId)
    }
    if (departmentId) {
      setValue('departmentId', staffOrDeptId)
    }
  }

  const handleClose = () => {
    setId(undefined)
    setParams({})
    setIsPreview(false)
    reset()
  }

  return (
    <>
      <ResponsiveContainer width={width} height={height} className={'overall-chart'}>
        <ComposedChart
          data={data}
          {...props}
          margin={{
            left: 10,
            bottom: 20,
            right: layout === 'horizontal' ? 35 : 0,
            top: layout === 'horizontal' ? 10 : 30
          }}
          layout={layout === 'horizontal' ? 'vertical' : 'horizontal'}
          className={layout === 'vertical' ? 'chart-horizontal' : 'chart-vertical'}
        >
          <CartesianGrid strokeDasharray='3 3' />
          {layout === 'horizontal' && (
            <>
              <XAxis type='number' tickCount={12} domain={[0, 110]}>
                <Label value={'(%)'} offset={-10} dx={25} dy={-23} position={'insideBottomRight'} />
              </XAxis>
              <YAxis
                dataKey='groupName'
                type='category'
                tickLine={true}
                tick={<CustomizedAxisTick />}
                interval='preserveStartEnd'
                onClick={(e) => handleClickLable(e)}
              />
            </>
          )}
          {layout === 'vertical' && (
            <>
              <ReferenceLine
                x={(data as any)?.[0]?.groupName}
                label={(e) => CustomLabel({ ...e, title: `(%)`, dy: 20, dx: 35 })}
              />

              {data?.length > 10 ? (
                <XAxis
                  dataKey='groupName'
                  type='category'
                  angle={-45}
                  textAnchor='end'
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  tick={<CustomizedAxisTickOverall />}
                  onClick={(e) => handleClickLable(e)}
                />
              ) : (
                <XAxis dataKey='groupName' type='category' onClick={(e) => handleClickLable(e)} />
              )}
              <YAxis tickCount={12} domain={[0, 110]} interval={0} />
            </>
          )}

          <Legend content={renderBarLegend} style={{ bottom: 0 }} />
          {chartProps.map((cp) => {
            return (
              <Bar
                radius={layout === 'vertical' ? [4, 4, 0, 0] : [0, 4, 4, 0]}
                key={cp.key}
                yAxisId={cp?.yAxisId}
                dataKey={cp.key}
                barSize={22}
                fill={colors?.[cp.key]}
                hide={!cp.display}
                onMouseEnter={(e) => {
                  return openTooltip({
                    content: tooltipContent(e.tooltipPayload[0])
                  })
                }}
                onMouseLeave={() => closeTooltip()}
              >
                <LabelList
                  dataKey={cp.key}
                  position={layout === 'vertical' ? 'top' : 'right'}
                  className='font-semibold text-[0.5rem]'
                  formatter={(v: number) => (v !== undefined ? v.toFixed(0) + '%' : '')}
                />
              </Bar>
            )
          })}
        </ComposedChart>
      </ResponsiveContainer>
      <BaseModal
        type={EModalType.INFO}
        className='w-4/5 h-4/5 max-w-4/5 [&>div]:h-full [&>*:first-child]:h-0 [&_.ant-modal-content]:h-full [&_.ant-modal-content]:flex [&_.ant-modal-content]:flex-col [&_.ant-modal-body]:flex [&_.ant-modal-body]:flex-col [&_.ant-modal-body]:flex-1 chart-modal'
        title={''}
        open={isPreview}
        isShowFooter={false}
        handleCancel={handleClose}
        onClose={handleClose}
        closeIcon={<IconBtnZoomIn />}
      >
        {isFetching || isLoading ? (
          <>
            <Skeleton paragraph={{ rows: 0 }} className='mb-4' />
            <Skeleton paragraph={{ rows: 8 }} />
          </>
        ) : (
          <div className='flex flex-col h-[95%]'>
            <form className='mb-8'>
              <BaseTreeSelect
                treeData={SIDE_PANEL_TREE_OPTIONS}
                name={'period'}
                control={control}
                treeCheckable={true}
                placeholder={t('common.period')}
                className={'w-56'}
              />
            </form>
            <BaseMultipleChart
              data={dataChartMultiple?.data || []}
              finalKpiScore={dataChartMultiple?.finalKpiScore || 0}
              isStaff={!!getValues('staffId')}
            />
          </div>
        )}
      </BaseModal>
    </>
  )
}

export default BaseBarChart
